// src/components/Box/Box.js
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import EditModal from "./EditModal";
import PersonEditPage from "../Person/PersonEditPage";
import EntityEditPage from "../Entity/EntityEditPage";
import apiService from "../../api";
import ConfirmModal from "../Common/ConfirmModal";
import { useConfirm } from "../../hooks/useConfirm";
import "./Box.css";

// Définition des champs de formulaire
const INDIVIDUAL_FIELDS = [
  { name: "first_name", label: "Prénom", type: "text" },
  { name: "last_name", label: "Nom", type: "text" },
  { name: "marital_status", label: "Statut marital", type: "text" },
  { name: "children_count", label: "Enfants à charge", type: "number" },
  { name: "employment_type", label: "Type d'emploi", type: "text" },
  { name: "fiscal_residence", label: "Résidence fiscale", type: "text" },
];

const ENTITY_FIELDS = [
  { name: "entity_name", label: "Dénomination", type: "text" },
  { name: "entity_type", label: "Forme juridique", type: "text" },
  { name: "tax_regime", label: "Régime fiscal", type: "text" },
  { name: "activity_type", label: "Type d'activité", type: "text" },
  { name: "fiscal_year_end", label: "Clôture exercice", type: "text" },
  { name: "share_capital", label: "Capital social (€)", type: "number" },
];

const Box = ({
  box,
  onRemove,
  onClick,
  isSelected,
  inSidebar,
  startLineDrawing,
  endLineDrawing,
  onUpdate,
  onDragStart,
  laneId,
  scenarioId,
  entities = [],
  isPrimary = false,
  onSetPrimary,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [boxData, setBoxData] = useState({
    ...box,
    first_name: box.first_name || '',
    last_name: box.last_name || '',
    marital_status: box.marital_status || 'Célibataire',
    children_count: box.children_count || 0,
    single_parent: box.single_parent || false,
    employment_type: box.employment_type || '',
    entity_name: box.entity_name || '',
    entity_type: box.entity_type || '',
    tax_regime: box.tax_regime || 'IS',
    activity_type: box.activity_type || '',
    fiscal_year_end: box.fiscal_year_end || '31/12',
    share_capital: box.share_capital || 0,
    fiscal_residence: box.fiscal_residence || ''
  });

  const isIndividual = box.content === "Individu";
  const isEntity = box.content === "Entity";

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  // Utiliser les champs statiques selon le type
  const fields = isIndividual ? INDIVIDUAL_FIELDS : isEntity ? ENTITY_FIELDS : [];

  useEffect(() => {
    // Charger les données depuis l'API si la box a un ID (déjà enregistrée)
    if (!inSidebar && box.id) {
      const fetchData = isIndividual ? apiService.getIndividual : apiService.getEntity;
      fetchData(box.id)
        .then(data => setBoxData(prev => ({ ...prev, ...data })))
        .catch(console.error);
    }
  }, [inSidebar, box.id, isIndividual, isEntity]);

  const handleMouseDown = (e) => {
    // Ne pas déclencher le dessin de ligne si on clique sur un bouton
    if (e.target.tagName === 'BUTTON') return;

    if (!inSidebar && startLineDrawing) {
      startLineDrawing(
        { laneId: laneId || box.laneId, boxId: box.uniqueBoxId },
        {
          x: e.clientX,
          y: e.clientY,
        }
      );
    }
  };

  const handleMouseUp = (e) => {
    if (!inSidebar && endLineDrawing) {
      endLineDrawing(
        { laneId: laneId || box.laneId, boxId: box.uniqueBoxId },
        {
          x: e.clientX,
          y: e.clientY,
        }
      );
    }
  };

  const handleDragStart = (e) => {
    if (inSidebar && onDragStart) {
      onDragStart(e);
    } else if (inSidebar) {
      e.dataTransfer.setData("boxId", box.id);
      e.dataTransfer.setData("boxContent", box.content);
      e.dataTransfer.setData("boxHeaderColor", box.headerColor);
    }
  };

  const openEditModal = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const closeEditModal = () => setIsEditing(false);

  const handleSave = async (updatedData) => {
    try {
      let savedData;
      const createData = {
        ...updatedData,
        scenario_id: scenarioId,
        lane_position: laneId,
        box_position: box.boxPosition || 0
      };

      if (isIndividual) {
        if (box.id || box.individual_id) {
          savedData = await apiService.updateIndividual(box.id || box.individual_id, updatedData);
        } else {
          savedData = await apiService.createIndividual(createData);
        }
      } else if (isEntity) {
        if (box.id || box.entity_id) {
          savedData = await apiService.updateEntity(box.id || box.entity_id, updatedData);
        } else {
          savedData = await apiService.createEntity(createData);
        }
      }
      setBoxData(savedData);
      onUpdate(box.uniqueBoxId, savedData);
      closeEditModal();
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const renderHeader = () => {
    const hasIncomingRelations =
      box.incomingRelations && box.incomingRelations.length > 0;
    return (
      <div className="box-header" style={{ backgroundColor: box.headerColor }}>
        {hasIncomingRelations && (
          <div className="box-header-subbands">
            {box.incomingRelations.map((relation, index) => (
              <div
                key={relation.id}
                className="subband"
                style={{
                  width: `${relation.percentage}%`,
                  backgroundColor:
                    relation.color || `hsl(${index * 30}, 70%, 70%)`,
                }}
                data-relation-id={relation.id}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`box ${isSelected ? "box-selected" : ""} ${isPrimary && isIndividual ? "is-primary-person" : ""}`}
      onClick={() => !inSidebar && onClick()}
      draggable={inSidebar}
      onDragStart={handleDragStart}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      data-box-id={box.uniqueBoxId}
      data-lane-id={laneId || box.laneId}
      data-deleted-shareholding-ids={JSON.stringify(box._deletedShareholdingIds || [])}
    >
      {renderHeader()}

      {/* Action buttons in header area */}
      {!inSidebar && (
        <div className="box-actions">
          <button
            className="edit-box-btn"
            onClick={openEditModal}
            title="Modifier"
          >
            ✎
          </button>
          <a
            className="open-tab-btn"
            href={isIndividual ? `/person/${box.individual_id || box.id}` : `/entity/${box.entity_id || box.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Ouvrir dans un nouvel onglet"
          >
            ↗
          </a>
          <button
            className="remove-box-btn"
            onClick={(e) => {
              e.stopPropagation();
              const itemName = isIndividual
                ? `${box.first_name || ''} ${box.last_name || ''}`.trim() || 'cet individu'
                : box.entity_name || 'cette entité';
              showConfirm({
                title: isIndividual ? 'Supprimer cette personne ?' : 'Supprimer cette entité ?',
                message: `Voulez-vous vraiment supprimer "${itemName}" ?`,
                details: ['Cette action supprimera également toutes les relations associées.'],
                confirmText: 'Supprimer',
                type: 'danger',
                onConfirm: () => onRemove(box.uniqueBoxId)
              });
            }}
            title="Supprimer"
          >
            ×
          </button>
        </div>
      )}

      <div className="box-content">
        {inSidebar ? (
          <h3>{box.content}</h3>
        ) : (
          <div className="box-content-centered">
            {isIndividual ? (
              <>
                <h3 className="box-title">
                  {onSetPrimary && (
                    <button
                      className={`primary-star-btn ${isPrimary ? 'is-primary' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetPrimary(box.individual_id || box.id);
                      }}
                      title={isPrimary ? 'Personne principale' : 'Définir comme personne principale'}
                    >
                      {isPrimary ? '⭐' : '☆'}
                    </button>
                  )}
                  {boxData.first_name || box.first_name || "Prénom"} {boxData.last_name || box.last_name || "Nom"}
                </h3>
                <p className="box-subtitle">{boxData.marital_status || box.marital_status || ""}</p>
              </>
            ) : isEntity ? (
              <>
                <h3 className="box-title">{boxData.entity_name || box.entity_name || "Société"}</h3>
                <p className="box-subtitle">
                  {boxData.entity_type || box.entity_type || ""}
                  {(boxData.tax_regime || box.tax_regime) && ` - ${boxData.tax_regime || box.tax_regime}`}
                </p>
              </>
            ) : (
              <h3>{box.content}</h3>
            )}
          </div>
        )}
      </div>

      {/* Person Edit Page - full screen for ALL individuals (new and existing) */}
      {isEditing && isIndividual && ReactDOM.createPortal(
        <PersonEditPage
          individualId={box.individual_id || box.id || null}
          onClose={closeEditModal}
          onSave={(updatedData) => {
            setBoxData(prev => ({ ...prev, ...updatedData }));
            if (onUpdate) onUpdate(box.uniqueBoxId, updatedData);
          }}
          // Props for creation mode
          initialData={boxData}
          scenarioId={scenarioId}
          laneId={laneId}
          boxPosition={box.boxPosition || 0}
          // Pass deleted shareholding IDs for instant UI filtering
          deletedShareholdingIds={box._deletedShareholdingIds || []}
        />,
        document.body
      )}

      {/* Entity Edit Page - full screen for ALL entities (new and existing) */}
      {isEditing && isEntity && ReactDOM.createPortal(
        <EntityEditPage
          entityId={box.entity_id || box.id || null}
          onClose={closeEditModal}
          onSave={(updatedData) => {
            setBoxData(prev => ({ ...prev, ...updatedData }));
            if (onUpdate) onUpdate(box.uniqueBoxId, updatedData);
          }}
          clearedManagerRoles={box._clearedManagerRoles || []}
        />,
        document.body
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default Box;